import React from 'react';
import { Box, Alert, AlertTitle, Button, Typography, Paper } from '@mui/material';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * 顶层错误边界：捕获其下任意组件渲染期抛出的异常，
 * 避免 React 整页白屏（如 v2.0.17 引入的 useMemo 缺失导致报表查询白屏）。
 * 注意：hooks/事件回调中的异常不会被捕获，仅渲染期异常会被兜住。
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // 生产环境可在此上报到监控平台；此处仅记录到控制台
    console.error('[ErrorBoundary] 捕获到渲染期异常:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    const message = this.state.error ? this.state.error.message : '';
    const isChunkError = /Failed to fetch dynamically imported module|Loading chunk|error loading dynamically imported module|Importing a module script failed/i.test(message);
    if (isChunkError) {
      // chunk 加载失败时，普通 reload 可能仍命中浏览器缓存的旧主包，
      // 用时间戳戳穿缓存，强制请求最新的 index.html。
      const url = new URL(window.location.href);
      url.searchParams.set('_t', String(Date.now()));
      window.location.replace(url.toString());
      return;
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const message = err ? err.message : '';
      const isChunkError = /Failed to fetch dynamically imported module|Loading chunk|error loading dynamically imported module|Importing a module script failed/i.test(message);
      return (
        <Box sx={{ p: 4, maxWidth: 720, mx: 'auto', mt: 8 }}>
          <Alert severity="error">
            <AlertTitle>页面出错了</AlertTitle>
            {isChunkError
              ? '页面脚本加载失败（常见于部署后旧标签页仍在引用旧文件），请点击下方「刷新页面」重新加载。'
              : '应用遇到一个意外错误，已被顶层错误边界捕获，已避免整页白屏。你可以尝试恢复，或刷新页面。'}
          </Alert>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={this.handleReset}>
              尝试恢复
            </Button>
            <Button variant="outlined" onClick={this.handleReload}>
              刷新页面
            </Button>
          </Box>
          {message && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: 'grey.100', overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                错误详情
              </Typography>
              <Typography
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', fontSize: 12, m: 0 }}
              >
                {err && err.stack ? err.stack : message}
              </Typography>
            </Paper>
          )}
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
